/**
 * Turning whatever someone picked from their device into something a room can
 * actually show.
 *
 * Two things stand between a camera roll and a wall:
 *
 * **HEIC**, which is what an iPhone stores photos as. No browser but Safari
 * can decode it, and the API refuses it too, because it only accepts formats
 * it can identify from their own leading bytes. So a HEIC is decoded here, in
 * the picker, and what leaves the browser is an ordinary JPEG.
 *
 * **Size.** A photo off a recent phone is routinely 10–25 MB and several
 * thousand pixels across — well past what the API accepts, and far past what
 * a room 1920 units wide can show. Anything too big is drawn down to a
 * sensible size first, so a normal photo simply works instead of coming back
 * as "that image is too large".
 *
 * Small images in formats the API already takes are passed through untouched:
 * re-encoding a PNG would cost it its transparency for no gain.
 *
 * The HEIC decoder is around 3 MB, so it is imported only once someone
 * actually picks a HEIC. The `/csp` build is the one we want: it is free of
 * `eval`, which our Content-Security-Policy does not permit.
 */

/** What the file pickers accept. HEIC is included because we convert it. */
export const IMAGE_ACCEPT =
  "image/png,image/jpeg,image/gif,image/webp,image/heic,image/heif,.heic,.heif";

/** Quality of the JPEG we produce. Visually indistinguishable from the source. */
const JPEG_QUALITY = 0.9;

/**
 * Longest edge we will upload, in pixels. Comfortably above the room's own
 * 1920-unit width, so a photo still has detail when someone zooms into it.
 */
const MAX_EDGE = 2560;

/**
 * Files at or under this go up as they are. It matches the API's own ceiling,
 * so the only images we re-encode are the ones that would otherwise be
 * refused.
 */
const PASS_THROUGH_BYTES = 8 * 1024 * 1024;

/**
 * ISO base-media brands that mean "this is a HEIF-family still image".
 * `mif1` and `msf1` are the generic ones an iPhone also writes.
 */
const HEIF_BRANDS = new Set([
  "heic",
  "heix",
  "heim",
  "heis",
  "hevc",
  "hevx",
  "hevm",
  "hevs",
  "mif1",
  "msf1",
  "heif",
]);

/**
 * Reads the file's own leading bytes rather than trusting its name or the
 * type the OS attached — both are routinely wrong for photos off a phone,
 * and a name is the one part of a file anyone can write.
 */
async function isHeif(file: File): Promise<boolean> {
  const header = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  if (header.length < 12) return false;

  const ascii = (from: number, to: number) =>
    String.fromCharCode(...header.subarray(from, to));

  return ascii(4, 8) === "ftyp" && HEIF_BRANDS.has(ascii(8, 12));
}

/** Draws a bitmap down to `MAX_EDGE` if it is bigger, and encodes it as JPEG. */
async function toJpeg(bitmap: ImageBitmap, name: string): Promise<File> {
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("Couldn't read that photo.");
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
  );
  if (!blob) throw new Error("Couldn't read that photo.");

  return new File([blob], `${name.replace(/\.[^.]+$/, "")}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

/**
 * The file to actually upload: the original, or a JPEG rendered from it.
 *
 * Throws with a message worth showing if a photo cannot be decoded, so the
 * caller can say so rather than uploading something that would be refused.
 */
export async function toUploadableImage(file: File): Promise<File> {
  if (await isHeif(file)) {
    let bitmap: ImageBitmap;
    try {
      const { heicTo } = await import("heic-to/csp");
      bitmap = await heicTo({ blob: file, type: "bitmap" });
    } catch {
      throw new Error("Couldn't read that HEIC photo. Try exporting it as a JPEG.");
    }
    return toJpeg(bitmap, file.name);
  }

  if (file.size <= PASS_THROUGH_BYTES) return file;

  try {
    return await toJpeg(await createImageBitmap(file), file.name);
  } catch {
    throw new Error("That photo is too large, and couldn't be shrunk. Try a smaller one.");
  }
}
